import React from 'react';
import classNames from 'classnames';

import SectionCard from '~components/SectionCard';
import Donut, { DonutProps } from '~components/visualisation/donut/Donut';
import TrackerLegend, { TrackerLegendItem } from '~support-at-home/components/budgets/tracker/TrackerLegend';

interface ComponentProps {
  title: string;
  description?: React.ReactNode;
  /** Inline after the title, e.g. a SectionInfo icon. */
  titleAddon?: React.ReactNode;
  className?: string;
  dataCy?: string;
  /** The ring. Omit for the empty state: an empty ring with a dash, and a large N/A where the figures would be. */
  donut?: DonutProps;
  /** The keyed figures beside the ring. */
  legend?: TrackerLegendItem[];
  /** Work is beyond the ring's limit (its second lap is drawn): the card gets the amber outline (TAV-3482). */
  over?: boolean;
  /** The empty ring's accessible name, e.g. "This budget has no care management". */
  emptyLabel?: string;
  /** Shown large and gray in place of the figures when there is no ring. Default "N/A". */
  emptyText?: string;
  emptyDataCy?: string;
  /** In place of the ring and figures, e.g. a loading spinner or an error with Retry (the SaH Dashboard cards). */
  pending?: React.ReactNode;
}

/**
 * A card with a donut and its keyed figures beside it (SaH Budget Tracker and SaH Dashboard, TAV-3442): the shared
 * shape of Budget Overview, Company Budget Overview and both Care Management Overviews. Styled by figure-donut-* in
 * budget-summary/_figure-sections.scss.
 */
const DonutFiguresCard: React.FC<ComponentProps> = ({
  title,
  description,
  titleAddon,
  className,
  dataCy,
  donut,
  legend = [],
  over = false,
  emptyLabel = 'No figures for this period',
  emptyText = 'N/A',
  emptyDataCy,
  pending,
}) => (
  <SectionCard
    title={title}
    description={description}
    titleAddon={titleAddon}
    className={classNames('figure-donut-card', { 'figure-over-card': over }, className)}
    dataCy={dataCy}
  >
    {pending}
    {!pending && donut && (
      <div className="figure-donut-layout">
        <Donut {...donut} />
        <div className="figure-donut-legend">
          <TrackerLegend items={legend} />
        </div>
      </div>
    )}
    {!pending && !donut && (
      <div className="figure-donut-layout">
        <Donut segments={[]} centreValue="-" ariaLabel={emptyLabel} />
        <p className="figure-donut-empty" data-cy={emptyDataCy}>
          {emptyText}
        </p>
      </div>
    )}
  </SectionCard>
);

export default DonutFiguresCard;
